import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users, Wallet, Network, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KYC_DOCS, uploadKycDoc, KycDocsPanel } from "@/components/KycDocs";
import { PageHeader, Card, Field, Select, Btn, MpinField, DataTable, FilterBar, StatusBadge, txnColumns, ReceiptDialog } from "@/components/shared";

function useRetailers() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/dt/retailers").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  return [rows, load];
}

function Stat({ icon: I, label, value, testid }) {
  return (
    <Card className="!p-5">
      <div className="np-label flex items-center gap-1.5"><I className="h-3.5 w-3.5" />{label}</div>
      <div className="mt-1 font-mono text-2xl font-semibold" data-testid={testid}>{value}</div>
    </Card>
  );
}

export function DtHome() {
  const { user, refreshBalance } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dt/dashboard").then(({ data }) => setD(data)); refreshBalance(); }, [refreshBalance]);
  return (
    <div>
      <PageHeader title={`Namaste, ${user.name.split(" ")[0]}`} subtitle="Your retailer network at a glance.">
        <Link to="/dt/fund-transfer"><Btn data-testid="dt-quick-transfer-button"><Wallet className="h-4 w-4" />Transfer Funds</Btn></Link>
      </PageHeader>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl bg-gradient-to-br from-blue-700 to-indigo-900 p-5 text-white fade-up">
          <div className="text-[11px] font-semibold uppercase tracking-widest opacity-80">My Wallet</div>
          <div className="mt-2 font-mono text-3xl font-semibold" data-testid="dt-wallet-balance">{d ? inr(d.balance) : "-"}</div>
          <Link to="/fund-request" className="mt-3 inline-block text-xs font-semibold hover:underline" data-testid="dt-add-money-link">+ Request funds from admin</Link>
        </div>
        <Stat icon={Users} label="Retailers" value={d ? `${d.active} / ${d.retailers} active` : "-"} testid="dt-retailer-count" />
        <Stat icon={Network} label="Network wallet float" value={d ? inr(d.network_balance) : "-"} testid="dt-network-balance" />
        <Stat icon={TrendingUp} label="Network business today" value={d ? inr(d.today.reduce((s, t) => s + t.amount, 0)) : "-"} testid="dt-today-business" />
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <Link to="/dt/earnings"><Stat icon={TrendingUp} label="My earning this month" value={d ? inr(d.month_earning) : "-"} testid="dt-month-earning" /></Link>
        <Link to="/dt/pending-dmt"><Stat icon={Wallet} label="Pending DMT in network" value={d ? d.pending_dmt : "-"} testid="dt-pending-count" /></Link>
        <Stat icon={Network} label="Sub-distributors / network size" value={d ? `${d.distributors} / ${d.network_size}` : "-"} testid="dt-extra-stat" />
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card title="Today by service">
          {(d?.today || []).length === 0 && <div className="text-sm text-slate-400">No network transactions today.</div>}
          {(d?.today || []).map((t) => <div key={t.service} className="flex justify-between border-b border-slate-100 py-2 text-sm last:border-0"><span>{t.service} ×{t.count}</span><b className="font-mono">{inr(t.amount)}</b></div>)}
        </Card>
        <Card title="Top retailers this month">
          {(d?.top_retailers || []).length === 0 && <div className="text-sm text-slate-400">No data yet.</div>}
          {(d?.top_retailers || []).map((t, i) => <div key={t.login_id} className="flex justify-between border-b border-slate-100 py-2 text-sm last:border-0"><span><b className="mr-2 text-slate-400">{i + 1}</b>{t.login_id} ×{t.count}</span><b className="font-mono">{inr(t.amount)}</b></div>)}
        </Card>
        <Card title="Recent fund transfers" action={<Link to="/dt/transfer-history" className="text-xs font-semibold text-blue-700" data-testid="dt-view-transfers-link">View all</Link>}>
          {(d?.recent_transfers || []).length === 0 && <div className="text-sm text-slate-400">No transfers yet.</div>}
          {(d?.recent_transfers || []).map((t) => (
            <div key={t.id} className="flex justify-between border-b border-slate-100 py-2 text-sm last:border-0">
              <span>{t.retailer_login} <span className="text-xs text-slate-400">{t.type}</span></span>
              <b className={`font-mono ${t.type === "REVERSE" ? "text-red-600" : "text-emerald-700"}`}>{inr(t.amount)}</b>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

const CHILD = { super: [["distributor", "Distributor"], ["sr", "Sales Rep (SR)"], ["retailer", "Retailer"]], distributor: [["retailer", "Retailer"], ["sr", "Sales Rep (SR)"]], sr: [["retailer", "Retailer"]] };

function CreateRetailer({ open, onClose, onDone, role }) {
  const blank = { name: "", mobile: "", email: "", shop_name: "", city: "", state: "", password: "", mpin: "1234", role: "retailer", aadhaar: "", pan: "" };
  const [f, setF] = useState(blank);
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/dt/retailers", f);
      const failed = [];
      for (const [k, l] of KYC_DOCS) {
        try { await uploadKycDoc(`/dt/retailers/${data.id}/kyc-docs`, k, files[k]); } catch (err) { failed.push(`${l}: ${errMsg(err)}`); }
      }
      if (failed.length) toast.error(`${data.login_id} created but some documents failed — upload again from KYC Docs. ${failed.join("; ")}`);
      else toast.success(`${data.login_id} created — sent to admin for approval`);
      setF(blank); setFiles({}); onDone(); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="dt-create-retailer-dialog">
        <DialogHeader><DialogTitle className="font-display">Onboard new retailer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          {[["name", "Name"], ["mobile", "Mobile"], ["email", "Email"], ["shop_name", "Shop Name"], ["city", "City"], ["state", "State"], ["password", "Password"], ["mpin", "MPIN"]].map(([k, l]) => (
            <Field key={k} label={l}><input className="np-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} required={["name", "mobile", "password", "mpin"].includes(k)} data-testid={`dt-new-${k}-input`} /></Field>
          ))}
          <Field label="Aadhaar No."><input className="np-input font-mono" maxLength={12} inputMode="numeric" value={f.aadhaar} onChange={(e) => setF({ ...f, aadhaar: e.target.value.replace(/\D/g, "") })} required data-testid="dt-new-aadhaar-input" /></Field>
          <Field label="PAN No."><input className="np-input font-mono uppercase" maxLength={10} value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} required data-testid="dt-new-pan-input" /></Field>
          {KYC_DOCS.map(([k, l]) => (
            <Field key={k} label={`${l} photo`}><input type="file" accept="image/png,image/jpeg,image/webp" className="w-full text-xs" onChange={(e) => setFiles({ ...files, [k]: e.target.files[0] })} required data-testid={`dt-new-doc-${k}`} /></Field>
          ))}
          {CHILD[role].length > 1 && <Field label="User Type" className="col-span-2"><Select options={CHILD[role].map(([value, label]) => ({ value, label }))} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="dt-new-role-select" /></Field>}
          <Btn type="submit" loading={loading} className="col-span-2" data-testid="dt-new-retailer-submit">Create {Object.fromEntries(CHILD[role])[f.role]}</Btn>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DtRetailers() {
  const { user } = useAuth();
  const isSuper = user.role !== "sr";
  const [rows, load] = useRetailers();
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const toggle = async (r) => {
    try { await api.patch(`/dt/retailers/${r.id}/status`, { status: r.status === "active" ? "blocked" : "active" }); toast.success("Status updated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title={isSuper ? "My Network" : "My Retailers"} subtitle={`${rows.length} direct users in your network`}>
        <Btn onClick={() => setOpen(true)} data-testid="dt-create-retailer-button"><Plus className="h-4 w-4" />{isSuper ? "New User" : "New Retailer"}</Btn>
      </PageHeader>
      <DataTable testid="dt-retailers-table" rows={rows} columns={[
        { label: "Login ID", render: (r) => <span className="font-mono text-xs">{r.login_id}</span> }, { label: "Type", render: (r) => <span className="text-xs font-bold uppercase">{r.post_id}</span> },
        { label: "Name", key: "name" }, { label: "Mobile", key: "mobile" }, { label: "Shop", key: "shop_name" }, { label: "City", key: "city" },
        { label: "Main Wallet", render: (r) => <span className="font-mono">{inr(r.balance)}</span> },
        { label: "AEPS Wallet", render: (r) => <span className="font-mono">{inr(r.aeps_balance)}</span> },
        { label: "KYC", render: (r) => <StatusBadge status={r.kyc_status} /> },
        { label: "Status", render: (r) => <span className={`text-xs font-bold uppercase ${r.status === "active" ? "text-emerald-700" : r.status === "pending" ? "text-amber-600" : "text-red-600"}`} data-testid={`dt-status-${r.login_id}`}>{r.status === "pending" ? "Pending approval" : r.status}</span> },
        { label: "Actions", render: (r) => (
          <div className="flex gap-3 text-xs font-semibold">
            <Link className="text-blue-700" to={`/dt/fund-transfer?rid=${r.id}`} data-testid={`dt-transfer-${r.login_id}`}>Transfer</Link>
            <Link className="text-slate-700" to={`/dt/rt-statement?rid=${r.id}`} data-testid={`dt-report-${r.login_id}`}>Statement</Link>
            {["pending", "rejected"].includes(r.status) && <button className="text-blue-700" onClick={() => setDocs(r)} data-testid={`dt-kyc-docs-${r.login_id}`}>KYC Docs</button>}
            {["active", "blocked"].includes(r.status) && <button className="text-red-600" onClick={() => toggle(r)} data-testid={`dt-toggle-${r.login_id}`}>{r.status === "active" ? "Block" : "Unblock"}</button>}
          </div>) },
      ]} />
      <CreateRetailer open={open} onClose={() => setOpen(false)} onDone={load} role={user.role} />
      <Dialog open={!!docs} onOpenChange={(o) => !o && setDocs(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="dt-kyc-docs-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">KYC documents · {docs?.login_id}</DialogTitle></DialogHeader>
          {docs && <KycDocsPanel user={docs} uploadUrl={`/dt/retailers/${docs.id}/kyc-docs`} onChange={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DtFundTransfer() {
  const { wallet, refreshBalance } = useAuth();
  const [params] = useSearchParams();
  const [rows, load] = useRetailers();
  const blank = { retailer_id: params.get("rid") || "", amount: "", type: params.get("type") || "TRANSFER", remarks: "", mpin: "" };
  const [f, setF] = useState(blank);
  const reqId = useRef(crypto.randomUUID());
  useEffect(() => setF((p) => ({ ...p, type: params.get("type") || "TRANSFER" })), [params]);
  const [loading, setLoading] = useState(false);
  const rt = rows.find((r) => r.id === f.retailer_id);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/dt/fund-transfer", { ...f, amount: Number(f.amount), request_id: reqId.current });
      reqId.current = crypto.randomUUID();
      toast.success(`${data.type === "REVERSE" ? "Reversed" : "Transferred"} ${inr(data.amount)} · ${data.ref}`);
      setF({ ...blank, retailer_id: f.retailer_id });
      refreshBalance();
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div>
      <PageHeader title={f.type === "REVERSE" ? "Fund Revert" : "Fund Transfer"} subtitle="Load a downline user's main wallet from your wallet, or pull funds back (revert)." />
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Card>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="dt-transfer-form">
            <Field label="User" className="sm:col-span-2"><Select options={rows.map((r) => ({ value: r.id, label: `${r.login_id} · ${r.name} · ${r.mobile}` }))} placeholder="Select User" value={f.retailer_id} onChange={set("retailer_id")} required data-testid="dt-retailer-select" /></Field>
            <Field label="Type">
              <div className="flex rounded-md border border-slate-200 p-1">
                {[["TRANSFER", "Transfer"], ["REVERSE", "Reverse"]].map(([v, l]) => (
                  <button type="button" key={v} aria-pressed={f.type === v} onClick={() => setF({ ...f, type: v })} className={`flex-1 rounded px-3 py-1.5 text-xs font-semibold ${f.type === v ? (v === "REVERSE" ? "bg-red-600 text-white" : "bg-blue-700 text-white") : "text-slate-600"}`} data-testid={`dt-type-${v.toLowerCase()}`}>{l}</button>
                ))}
              </div>
            </Field>
            <Field label="Amount"><input type="number" className="np-input font-mono" value={f.amount} onChange={set("amount")} required data-testid="dt-amount-input" /></Field>
            <Field label="Remarks"><input className="np-input" value={f.remarks} onChange={set("remarks")} data-testid="dt-remarks-input" /></Field>
            <MpinField value={f.mpin} onChange={(v) => setF({ ...f, mpin: v })} testid="dt-mpin-input" />
            <Btn type="submit" loading={loading} variant={f.type === "REVERSE" ? "red" : "primary"} data-testid="dt-transfer-submit">{f.type === "REVERSE" ? "Reverse Funds" : "Transfer Funds"}</Btn>
          </form>
        </Card>
        <Card title="Balances">
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-blue-50 p-3"><div className="np-label">Your wallet</div><div className="font-mono text-xl font-semibold">{inr(wallet.balance)}</div></div>
            <div className="rounded-lg bg-slate-50 p-3" data-testid="dt-selected-retailer-balance"><div className="np-label">{rt ? `${rt.login_id} main wallet` : "Select a retailer"}</div><div className="font-mono text-xl font-semibold">{rt ? inr(rt.balance) : "-"}</div></div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export function DtTransferHistory() {
  const [retailers] = useRetailers();
  const [rows, setRows] = useState([]);
  const [rid, setRid] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    const { data } = await api.get("/dt/fund-transfers", { params: { ...f, retailer_id: rid || undefined } });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const cols = [
    { label: "Date", render: (r) => fmtDate(r.created_at), csv: (r) => r.created_at }, { label: "Ref", key: "ref", render: (r) => <span className="font-mono text-xs">{r.ref}</span> },
    { label: "Retailer", key: "retailer_login", render: (r) => `${r.retailer_login} · ${r.retailer_name}` }, { label: "Type", key: "type" },
    { label: "Amount", key: "amount", render: (r) => <span className={`font-mono font-semibold ${r.type === "REVERSE" ? "text-red-600" : "text-emerald-700"}`}>{inr(r.amount)}</span> },
    { label: "Remarks", key: "remarks" },
  ];
  return (
    <div>
      <PageHeader title="Fund Transfer History" />
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="dt-transfers" exportCols={cols}>
        <Field label="Retailer"><Select options={retailers.map((r) => ({ value: r.id, label: r.login_id }))} placeholder="All" value={rid} onChange={(e) => setRid(e.target.value)} data-testid="dt-history-retailer-filter" /></Field>
      </FilterBar>
      <DataTable testid="dt-transfer-history-table" rows={rows} columns={cols} />
    </div>
  );
}

const GROUPS = {
  recharge: { title: "Recharge Report", services: "Mobile Recharge,DTH" },
  bbps: { title: "Bill Payment Report", services: "BBPS,Credit Card Bill" },
  dmt: { title: "DMT Report", services: "DMT,Account Verification" },
  aeps: { title: "AEPS Report", services: "AEPS Cash Withdrawal,AEPS Balance Enquiry,AEPS Mini Statement,Aadhaar Pay,Cash Deposit" },
};

export function DtNetworkReport() {
  const [params] = useSearchParams();
  const group = GROUPS[params.get("group")];
  const [retailers] = useRetailers();
  const [rows, setRows] = useState([]);
  const [rid, setRid] = useState(params.get("rid") || "");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [rec, setRec] = useState(null);
  const load = async (f = {}) => {
    setLoading(true);
    const { data } = await api.get("/dt/transactions", { params: { ...f, retailer_id: rid || undefined, status: status || undefined, service: group?.services } });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [group]); // eslint-disable-line
  const cols = [{ label: "Retailer", key: "login_id" }, ...txnColumns(setRec)];
  return (
    <div>
      <PageHeader title={group ? group.title : "Network Report"} subtitle="Transactions done by users in your network." />
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="network-report" exportCols={cols.slice(0, 11)}>
        <Field label="Retailer"><Select options={retailers.map((r) => ({ value: r.id, label: r.login_id }))} placeholder="All" value={rid} onChange={(e) => setRid(e.target.value)} data-testid="dt-report-retailer-filter" /></Field>
        <Field label="Status"><Select options={["SUCCESS", "PENDING", "FAILED", "REFUNDED"]} placeholder="All" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="dt-report-status-filter" /></Field>
      </FilterBar>
      <DataTable testid="dt-network-table" rows={rows} columns={cols} />
      <ReceiptDialog txn={rec} onClose={() => setRec(null)} />
    </div>
  );
}

const fmtD = (s) => fmtDate(s);

export function DtTransferAll() {
  const { refreshBalance } = useAuth();
  const [q, setQ] = useState("");
  const [u, setU] = useState(null);
  const [f, setF] = useState({ amount: "", remarks: "", mpin: "" });
  const reqId = useRef(crypto.randomUUID());
  const [loading, setLoading] = useState(false);
  const search = async (e) => {
    e.preventDefault();
    try { const { data } = await api.get("/dt/lookup", { params: { q } }); setU(data); } catch (err) { setU(null); toast.error(errMsg(err)); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/dt/transfer-any", { user_id: u.id, amount: Number(f.amount), remarks: f.remarks, mpin: f.mpin, request_id: reqId.current });
      reqId.current = crypto.randomUUID();
      toast.success(`Transferred ${inr(data.amount)} to ${data.retailer_login} · ${data.ref}`);
      setF({ amount: "", remarks: "", mpin: "" });
      setU({ ...u, balance: u.balance + data.amount });
      refreshBalance();
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div>
      <PageHeader title="Fund Transfer To All" subtitle="Search any retailer or distributor by mobile number or login ID and transfer balance." />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <form onSubmit={search} className="flex items-end gap-3" data-testid="dt-search-form">
            <Field label="Mobile / Login ID" className="flex-1"><input className="np-input font-mono" value={q} onChange={(e) => setQ(e.target.value)} required data-testid="dt-search-input" /></Field>
            <Btn type="submit" data-testid="dt-search-button">Search</Btn>
          </form>
          {u && (
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="dt-transfer-all-form">
              <Field label="Amount"><input type="number" className="np-input font-mono" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required data-testid="dt-all-amount-input" /></Field>
              <Field label="Remarks"><input className="np-input" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="dt-all-remarks-input" /></Field>
              <MpinField value={f.mpin} onChange={(v) => setF({ ...f, mpin: v })} testid="dt-all-mpin-input" />
              <Btn type="submit" loading={loading} data-testid="dt-all-submit">Transfer</Btn>
            </form>
          )}
        </Card>
        <Card title="User Details">
          {!u ? <div className="py-10 text-center text-sm text-slate-400">Search a user to see details.</div> : (
            <div className="grid grid-cols-2 gap-4 text-sm" data-testid="dt-found-user">
              {[["Login ID", u.login_id], ["Name", u.name], ["Mobile", u.mobile], ["Type", u.role], ["Shop", u.shop_name || "-"], ["Balance", inr(u.balance)]].map(([k, v]) => (
                <div key={k}><div className="np-label">{k}</div><div className="font-semibold capitalize">{v}</div></div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

const SUMMARY_TYPES = [{ value: "", label: "All" }, { value: "Revenue", label: "Revenue" }, { value: "DR", label: "Debit" }, { value: "CR", label: "Credit" }];

export function DtFundSummary() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    const params = { ...f, ...(type === "Revenue" ? { service: "Commission" } : type ? { type } : {}) };
    const { data } = await api.get("/ledger", { params });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const cols = [
    { label: "Date", render: (r) => fmtD(r.created_at), csv: (r) => r.created_at }, { label: "ID", key: "ref", render: (r) => <span className="font-mono text-xs">{r.ref}</span> },
    { label: "Transaction Type", key: "service" }, { label: "Description", key: "description" },
    { label: "Commission", key: "commission", render: (r) => <span className="font-mono text-emerald-700">{inr(r.commission)}</span> },
    { label: "Debit", render: (r) => r.type === "DR" ? <span className="font-mono text-red-600">{inr(r.amount)}</span> : "", csv: (r) => (r.type === "DR" ? r.amount : "") },
    { label: "Credit", render: (r) => r.type === "CR" ? <span className="font-mono text-emerald-700">{inr(r.amount)}</span> : "", csv: (r) => (r.type === "CR" ? r.amount : "") },
    { label: "Balance", key: "closing", render: (r) => <span className="font-mono font-semibold">{inr(r.closing)}</span> },
  ];
  const cr = rows.filter((r) => r.type === "CR").reduce((s, r) => s + r.amount, 0);
  const dr = rows.filter((r) => r.type === "DR").reduce((s, r) => s + r.amount, 0);
  return (
    <div>
      <PageHeader title="Fund Summary" />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Card><div className="np-label">Total Credit</div><div className="font-mono text-lg font-semibold text-emerald-700" data-testid="fs-total-credit">{inr(cr)}</div></Card>
        <Card><div className="np-label">Total Debit</div><div className="font-mono text-lg font-semibold text-red-600" data-testid="fs-total-debit">{inr(dr)}</div></Card>
        <Card><div className="np-label">Revenue (commission)</div><div className="font-mono text-lg font-semibold">{inr(rows.reduce((s, r) => s + (r.commission || 0), 0))}</div></Card>
      </div>
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="fund-summary" exportCols={cols}>
        <Field label="Type"><Select options={SUMMARY_TYPES} value={type} onChange={(e) => setType(e.target.value)} data-testid="fs-type-filter" /></Field>
      </FilterBar>
      <DataTable testid="fund-summary-table" rows={rows} columns={cols} />
    </div>
  );
}

export function DtRtStatement() {
  const [params] = useSearchParams();
  const [users] = useNetwork();
  const [rid, setRid] = useState(params.get("rid") || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    if (!rid) return toast.error("Please select a retailer");
    setLoading(true);
    try { const { data } = await api.get("/dt/retailer-ledger", { params: { ...f, retailer_id: rid } }); setRows(data); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { if (rid) load(); }, []); // eslint-disable-line
  const cols = [
    { label: "Date", render: (r) => fmtD(r.created_at), csv: (r) => r.created_at }, { label: "UID", key: "ref", render: (r) => <span className="font-mono text-xs">{r.ref}</span> },
    { label: "Type", key: "service" }, { label: "Wallet", key: "wallet", render: (r) => <span className="text-xs font-bold uppercase">{r.wallet}</span> },
    { label: "Amount", key: "amount", render: (r) => <span className={`font-mono ${r.type === "CR" ? "text-emerald-700" : "text-red-600"}`}>{r.type} {inr(r.amount)}</span> },
    { label: "Commission", key: "commission", render: (r) => <span className="font-mono">{inr(r.commission)}</span> },
    { label: "Surcharge", key: "surcharge", render: (r) => <span className="font-mono">{inr(r.surcharge)}</span> },
    { label: "TDS", key: "tds", render: (r) => <span className="font-mono">{inr(r.tds)}</span> },
    { label: "Before Balance", key: "opening", render: (r) => <span className="font-mono">{inr(r.opening)}</span> },
    { label: "After Balance", key: "closing", render: (r) => <span className="font-mono font-semibold">{inr(r.closing)}</span> },
    { label: "Description", key: "description" },
  ];
  return (
    <div>
      <PageHeader title="RT Wise Statement" subtitle="Ledger of any user in your network." />
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="rt-statement" exportCols={cols}>
        <Field label="User"><Select options={users.map((r) => ({ value: r.id, label: `${r.login_id} · ${r.name} · ${r.mobile}` }))} placeholder="Select User" value={rid} onChange={(e) => setRid(e.target.value)} data-testid="rt-statement-user-select" /></Field>
      </FilterBar>
      <DataTable testid="rt-statement-table" rows={rows} columns={cols} />
    </div>
  );
}

function useNetwork() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/dt/network-users").then(({ data }) => setRows(data)); }, []);
  return [rows];
}

const EGROUPS = ["Recharge", "BBPS", "DMT", "AEPS"];

export function DtEarnings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    const { data } = await api.get("/dt/earnings", { params: f });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const cell = (g, k) => (r) => <span className="font-mono">{inr(r[g]?.[k])}</span>;
  const cols = [
    { label: "Date", key: "date" },
    ...EGROUPS.flatMap((g) => [
      { label: `${g} Amount`, render: cell(g, "amount"), csv: (r) => r[g]?.amount || 0 },
      { label: `${g} Comm.`, render: cell(g, "commission"), csv: (r) => r[g]?.commission || 0 },
    ]),
    { label: "Total Commission", key: "total_commission", render: (r) => <span className="font-mono text-emerald-700">{inr(r.total_commission)}</span> },
    { label: "TDS", key: "total_tds", render: (r) => <span className="font-mono">{inr(r.total_tds)}</span> },
    { label: "Commission After TDS", key: "net", render: (r) => <span className="font-mono font-semibold">{inr(r.net)}</span> },
  ];
  return (
    <div>
      <PageHeader title="Earning Report" subtitle="Commission earned on your network's Recharge, BBPS, DMT and AEPS transactions.">
        <div className="rounded-lg bg-emerald-600 px-4 py-2 text-white"><div className="text-[10px] uppercase tracking-widest opacity-80">Net earning</div><div className="font-mono text-xl font-semibold" data-testid="dt-earning-total">{inr(rows.reduce((s, r) => s + r.net, 0))}</div></div>
      </PageHeader>
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="earning-report" exportCols={cols} />
      <DataTable testid="dt-earnings-table" rows={rows} columns={cols} />
    </div>
  );
}

function RefundDialog({ txn, onClose, onDone }) {
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const send = async () => {
    try { const { data } = await api.post(`/dt/pending-dmt/${txn.txn_id}/refund-otp`); toast.info(data.message); setSent(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async () => {
    setLoading(true);
    try { const { data } = await api.post(`/dt/pending-dmt/${txn.txn_id}/refund`, { otp }); toast.success(`Refunded ${inr(data.refunded)} to retailer`); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={!!txn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="refund-dialog">
        <DialogHeader><DialogTitle className="font-display">Refund {txn?.txn_id}</DialogTitle></DialogHeader>
        <div className="text-sm">{txn?.details?.bene_name} · {txn?.details?.account_no} · <b>{inr(txn?.amount)}</b></div>
        {!sent ? <Btn onClick={send} data-testid="refund-send-otp-button">Send OTP to customer</Btn> : (
          <div className="flex items-end gap-2">
            <Field label="Enter OTP" className="flex-1"><input className="np-input font-mono" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="refund-otp-input" /></Field>
            <Btn loading={loading} onClick={submit} data-testid="refund-confirm-button">Update</Btn>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DtPendingDmt() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [refund, setRefund] = useState(null);
  const load = async (f = {}) => {
    setLoading(true);
    const { data } = await api.get("/dt/pending-dmt", { params: { ...f, q: q || undefined } });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const cols = [
    { label: "TID", key: "txn_id", render: (r) => <span className="font-mono text-xs">{r.txn_id}</span> }, { label: "Rt", key: "login_id" },
    { label: "Remitter", key: "number" },
    { label: "Beneficiary Detail", render: (r) => `${r.details?.bene_name} · ${r.operator} · ${r.details?.account_no}`, csv: (r) => `${r.details?.bene_name} ${r.details?.account_no}` },
    { label: "Amount", key: "amount", render: (r) => <span className="font-mono font-semibold">{inr(r.amount)}</span> },
    { label: "Mode", render: (r) => r.details?.mode, csv: (r) => r.details?.mode }, { label: "Status", key: "status", render: (r) => <StatusBadge status={r.status} /> },
    { label: "Date", render: (r) => fmtD(r.created_at), csv: (r) => r.created_at },
    { label: "Refund", render: (r) => <Btn variant="outline" className="h-8 px-3 text-xs" onClick={() => setRefund(r)} data-testid={`refund-btn-${r.txn_id}`}>Refund</Btn> },
  ];
  return (
    <div>
      <PageHeader title="Refund Pending Transactions" subtitle="Pending DMT transactions in your network. Refund requires customer OTP." />
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="pending-dmt" exportCols={cols.slice(0, 8)}>
        <Field label="Txn ID / Account"><input className="np-input" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pending-search-input" /></Field>
      </FilterBar>
      <DataTable testid="dt-pending-table" rows={rows} columns={cols} />
      <RefundDialog txn={refund} onClose={() => setRefund(null)} onDone={load} />
    </div>
  );
}

export function Agreement() {
  const { user, reloadUser } = useAuth();
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const sign = async () => {
    setLoading(true);
    try { await api.post("/dt/agreement"); await reloadUser(); toast.success("Agreement accepted"); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Partner Agreement" subtitle="Please read and accept the agreement to continue using the panel." />
      <Card>
        <div className="max-h-[50vh] space-y-3 overflow-y-auto text-sm text-slate-700" data-testid="agreement-text">
          <p>This agreement is made between <b>ShubhPay Technologies</b> and <b>{user.name} ({user.login_id})</b>, {user.shop_name}.</p>
          <p>1. The partner shall onboard retailers only after verifying their identity and KYC documents.</p>
          <p>2. The partner is responsible for funds transferred to its downline and for recovering any dues.</p>
          <p>3. Commission is credited as per the company's slab and is subject to TDS as per Income Tax rules.</p>
          <p>4. The partner shall not charge customers anything beyond the published charges.</p>
          <p>5. Any fraud, misuse of AEPS/DMT services or data leakage will lead to termination and legal action.</p>
          <p>6. The company may revise commission, charges and these terms with prior notice.</p>
        </div>
        <label className="mt-5 flex items-center gap-2 text-sm"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="agreement-checkbox" />I have read and agree to the terms above.</label>
        <Btn className="mt-4" disabled={!ok} loading={loading} onClick={sign} data-testid="agreement-submit-button">Accept & Continue</Btn>
      </Card>
    </div>
  );
}

export function DtSalesReport() {
  const { user } = useAuth();
  const [users] = useNetwork();
  const [uid, setUid] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    try { const { data } = await api.get("/dt/sales-report", { params: { ...f, user_id: uid || undefined } }); setRows(data); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const subs = users.filter((u) => u.role !== "retailer");
  const cols = [
    { label: "Date", key: "date" }, { label: "Service", key: "service" }, { label: "Operator", key: "operator" }, { label: "Txns", key: "count" },
    { label: "Sale", key: "sale", render: (r) => <span className="font-mono font-semibold">{inr(r.sale)}</span> },
    { label: "Commission", key: "commission", render: (r) => <span className="font-mono text-emerald-700">{inr(r.commission)}</span> },
  ];
  return (
    <div>
      <PageHeader title={user.role === "super" ? "Sales Report DI Wise" : "Sales Report"} subtitle="Operator-wise sale and commission of your network.">
        <div className="rounded-lg bg-slate-900 px-4 py-2 text-white"><div className="text-[10px] uppercase tracking-widest opacity-80">Total sale</div><div className="font-mono text-xl font-semibold" data-testid="sales-total">{inr(rows.reduce((s, r) => s + r.sale, 0))}</div></div>
      </PageHeader>
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="sales-report" exportCols={cols}>
        <Field label="Distributor / SR"><Select options={subs.map((u) => ({ value: u.id, label: `${u.login_id} · ${u.name}` }))} placeholder="All network" value={uid} onChange={(e) => setUid(e.target.value)} data-testid="sales-user-filter" /></Field>
      </FilterBar>
      <DataTable testid="sales-report-table" rows={rows} columns={cols} />
    </div>
  );
}
